import { Equipment, Project } from '../types/project';
import { defaultEditionForState, getEditionPack } from './calc/editions';
import { existingLoads } from './calc/loadRoles';

// Sanity checks on the dwelling profile — the traditional path's answer to the
// measured path's data-quality panel. None of these changes a number. Each is
// a completion warning (acknowledged, never silent) and a Results-tab note.
// They exist because the no-data market this method serves has no inventory
// cross-check: without interval data nothing else says "this list is short",
// and a short list is the traditional method's false-PASS path
// (docs/specs/load-calc-program.md §6; audit 2026-09-02, items S2 to S4).

export type ProfileIssueId =
  | 'floor_area_band'
  | 'small_appliance_below_minimum'
  | 'laundry_below_minimum'
  | 'inventory_empty'
  | 'inventory_missing_categories'
  | 'dryer_laundry_flag_implausible'
  | 'demand_parameters_stated';

export interface ProfileIssue {
  id: ProfileIssueId;
  title: string;
  message: string;
  /** Completion-validation fieldPath — which tab owns the fix. */
  fieldPath: 'dwelling_floor_area_sqft' | 'existing_loads' | 'demand_parameters';
}

export const FLOOR_AREA_BAND = { min: 500, max: 10000 } as const;
/** 20 A × 120 V — the most a laundry branch circuit can serve. */
export const LAUNDRY_CIRCUIT_VA = 2400;

export function unitVA(eq: Equipment): number {
  if (eq.kw != null) return eq.kw * 1000;
  if (eq.amps != null) return eq.amps * (eq.voltage || 0);
  return 0;
}

const nameOf = (eq: Equipment) => eq.equipment_name || '';
export const isCookingEquipment = (eq: Equipment): boolean =>
  eq.equipment_type === 'Range' || /range|cooktop|oven|stove/i.test(nameOf(eq));
export const isWaterHeating = (eq: Equipment): boolean =>
  eq.equipment_type === 'Water Heater' || /water heater|tankless/i.test(nameOf(eq));
// Name matching is for free-text rows only. A water heater or a dryer is
// never space conditioning, however it is named: "Water heater (tank)"
// contains "heat", and the heat pump variants of both contain "heat pump".
// Before this exclusion, any water heater satisfied the heating-or-cooling
// check and silenced the missing-category warning.
export const isHeatingOrCooling = (eq: Equipment): boolean =>
  eq.equipment_type === 'Heat Pump' ||
  eq.equipment_type === 'Strip Heat' ||
  eq.equipment_type === 'A/C' ||
  (eq.equipment_type !== 'Water Heater' &&
    eq.equipment_type !== 'Dryer' &&
    !/water heater|dryer/i.test(nameOf(eq)) &&
    /furnace|boiler|heat|air cond|a\/c|hvac|mini.?split/i.test(nameOf(eq)));

/** A dryer flagged as served by the laundry branch circuit drops out of the
 * pool. A 240 V dryer, or one drawing more than that 20 A, 120 V circuit can
 * carry, cannot be on it. */
export function dryerFlagImplausible(eq: Equipment): boolean {
  return (
    eq.equipment_type === 'Dryer' &&
    eq.on_laundry_circuit === true &&
    ((eq.voltage || 0) >= 208 || unitVA(eq) > LAUNDRY_CIRCUIT_VA)
  );
}

export function profileQualityIssues(
  project: Partial<Project>,
  equipment: Equipment[]
): ProfileIssue[] {
  const issues: ProfileIssue[] = [];
  const pack = getEditionPack(
    project.code_edition ?? defaultEditionForState(project.customer_state)
  );
  const circuitsRef = pack.sectionMap.branchCircuitProvisions;
  const cite = circuitsRef ? ` (NEC ${circuitsRef})` : '';

  const area = project.dwelling_floor_area_sqft ?? 0;
  if (area > 0 && (area < FLOOR_AREA_BAND.min || area > FLOOR_AREA_BAND.max)) {
    issues.push({
      id: 'floor_area_band',
      title: `Floor area ${area} ft² is outside the typical range`,
      message: `Dwelling floor areas normally fall between ${FLOOR_AREA_BAND.min} and ${FLOOR_AREA_BAND.max} ft². Confirm the figure was measured by outside dimensions and entered in square feet; it drives the general lighting load.`,
      fieldPath: 'dwelling_floor_area_sqft',
    });
  }

  const smallAppliance = project.small_appliance_circuit_count ?? 2;
  if (smallAppliance < 2) {
    issues.push({
      id: 'small_appliance_below_minimum',
      title: 'Fewer than two small-appliance circuits counted',
      message: `The code requires at least two 20 A small-appliance branch circuits in a dwelling unit${cite}, and the calculation counts each at 1,500 VA. ${smallAppliance} entered. Confirm the count before relying on the result.`,
      fieldPath: 'dwelling_floor_area_sqft',
    });
  }
  const laundry = project.laundry_circuit_count ?? 1;
  if (laundry < 1) {
    issues.push({
      id: 'laundry_below_minimum',
      title: 'No laundry circuit counted',
      message: `The code requires a laundry branch circuit in a dwelling unit${cite}, counted at 1,500 VA. Enter 0 only where the dwelling genuinely has no laundry provision.`,
      fieldPath: 'dwelling_floor_area_sqft',
    });
  }

  const inventory = existingLoads(equipment);
  if (inventory.length === 0) {
    issues.push({
      id: 'inventory_empty',
      title: 'No fixed appliances listed',
      message:
        'The inventory is empty. A dwelling normally has cooking, water-heating and heating or cooling equipment on dedicated circuits; a calculation that omits them understates the existing load.',
      fieldPath: 'existing_loads',
    });
  } else {
    const missing: string[] = [];
    if (!inventory.some(isCookingEquipment)) missing.push('cooking equipment');
    if (!inventory.some(isWaterHeating)) missing.push('a water heater');
    if (!inventory.some(isHeatingOrCooling)) missing.push('heating or cooling equipment');
    if (missing.length > 0) {
      issues.push({
        id: 'inventory_missing_categories',
        title: `Inventory lists no ${missing.join(', no ')}`,
        message: `Most dwellings have ${missing.join(', ')} on dedicated circuits (a gas appliance still carries a small electrical load; use the gas variants). Confirm these are genuinely absent before attesting that the inventory is complete.`,
        fieldPath: 'existing_loads',
      });
    }
  }

  for (const eq of inventory.filter(dryerFlagImplausible)) {
    const label = eq.equipment_name || 'Dryer';
    issues.push({
      id: 'dryer_laundry_flag_implausible',
      title: `${label} cannot be on the laundry circuit`,
      message: `${label} is flagged as served by the laundry branch circuit, but a ${eq.voltage} V, ${Math.round(unitVA(eq))} VA dryer cannot be supplied by the 120 V, 20 A circuit counted at 1,500 VA. Untick the flag so the dryer counts at nameplate, unless it truly plugs into that circuit.`,
      fieldPath: 'existing_loads',
    });
  }

  return issues;
}

/** Everything the traditional basis wants acknowledged before completion. */
export function traditionalQualityIssues(
  project: Partial<Project>,
  equipment: Equipment[]
): ProfileIssue[] {
  // Was profile checks plus the demand-parameter election. The election went
  // with the editable parameters on 2026-09-06 — no UI can state one, so there
  // is nothing left to acknowledge. Kept as its own function because the
  // completion check and the Results tab must ask the same question.
  return profileQualityIssues(project, equipment);
}
